import { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { isAdmin } from '../lib/api'
import type { User } from '@supabase/supabase-js'

interface RequireAuthProps {
  children: React.ReactNode
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const location = useLocation()

  useEffect(() => {
    supabase.auth.getUser()
      .then(({ data }) => {
        setUser(data.user)
        setLoading(false)
        // Warm the isAdmin cache while the user is confirmed — so NavBar
        // renders with the correct state immediately on first paint
        if (data.user) isAdmin()
      })
      .catch(() => {
        // Stale/invalid refresh token — clear it and treat as logged out
        supabase.auth.signOut()
        setUser(null)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return <div className="min-h-screen bg-gray-950" />
  }

  if (!user) {
    const next = location.pathname !== '/' ? `?next=${encodeURIComponent(location.pathname)}` : ''
    return <Navigate to={`/login${next}`} replace />
  }

  return <>{children}</>
}
